/**
 * CoverageQaTask - Simplified QA interface for validating a labeler's product selection.
 *
 * This is a standalone QA review page (not a mode flag on CoverageTask) that provides
 * a focused workflow for quality assurance reviewers to validate coverage task submissions.
 *
 * ## Key Features
 * - **Single box per task**: Each QA task reviews one box, not the entire coverage result
 * - **50/50 split layout**: Left panel shows shelf image (auto-zoomed to box), right shows planogram
 * - **Multi-product selection**: Click products on planogram to toggle selection
 * - **Three actions**: Approve, correct product selection (with optional planogram change), or reject
 *
 * ## User Flow
 * 1. View shelf image zoomed to the box under review (RED border)
 * 2. View planogram with selected products highlighted (RED)
 * 3. Decide:
 *    - Everything correct → "Submit as Correct"
 *    - Wrong product → Click correct product(s) → "Submit Correction"
 *    - Wrong planogram → "Change Planogram" → select product(s) → "Submit Correction"
 *    - Image issue → "Mark Incorrect" dropdown → select reason
 *
 * ## State Management
 * - Product selections stored in local state, compared against original to detect changes
 * - Uses `useActivePlanoProductInfoStore` Zustand store for planogram product click events
 * - Resets all state after submission and fetches next pending task
 *
 * @see CoverageQaActionBar - Bottom action bar component
 * @see CoverageQaResult - Type definition for QA results
 * @see docs/coverage-qa-feature.md - Full feature documentation
 *
 * @route /jobs/:jobId/coverage-qa
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useProfileHome } from "@/hooks/useProfileHome";
import { ActionIcon, Button, LoadingOverlay, Text } from "@mantine/core";
import { IconArrowLeft, IconPlayerStopFilled } from "@tabler/icons-react";
import { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import { BoxDrawImage } from "@labeling-tools/ui-components/src/box-draw-image";
import {
  useFetchJob,
  useNextPendingTask,
  useCompleteJobTask,
  useFetchParcel,
  useImageGallery,
} from "@labeling-tools/api/lebron/react-query-hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useAtlasPlanogram } from "@labeling-tools/api/hooks";
import { useActivePlanoProductInfoStore } from "@labeling-tools/ui-components/src/planogram-viewer/use-active-plano-product-info-store";
import { PlanogramViewer } from "@labeling-tools/ui-components/src/planogram-viewer/planogram-viewer.comp";
import {
  CoverageSidebar,
  COVERAGE_SIDEBAR_WIDTH,
} from "@/components/coverage/CoverageSidebar";
import { CoverageQaActionBar } from "@/components/coverage/CoverageQaActionBar";
import { CoverageQaProductCard } from "@/components/coverage/CoverageQaProductCard";
import {
  extractBoxPoints,
  extractOriginalProductSelection,
} from "@/components/coverage/useCoverageQaData";
import {
  PlanogramSearchModal,
  type PlanogramSlotEdit,
} from "@/components/coverage/PlanogramSearchModal";
import { HEADER_HEIGHT, PLAY_MODE_JOB_TYPE } from "@/config/data";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setIsPlayMode } from "@/store";
import type {
  CoverageQaRejection,
  CoverageQaResult,
  ProductSelection,
} from "@/types/coverage";
import { useDevFixtures } from "@/dev";
import { resolveGalleryImageUrl } from "@/utils/helpers";
import { useFetchNextPlayModeJob } from "@/hooks/useFetchNextPlayModeJob";
import { useBounceToJobs } from "@/hooks/useBounceToJobs";

// Action bar height for bottom padding
const ACTION_BAR_HEIGHT = 64;

export const CoverageQaTask = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { jobsPath } = useProfileHome();
  const queryClient = useQueryClient();
  const zoomRef = useRef<ReactZoomPanPinchRef>(null);
  const dispatch = useAppDispatch();
  const isPlayMode = useAppSelector((state) => state.oos.isPlayMode);
  const [noNextJobAvailable, setNoNextJobAvailable] = useState(false);

  // Dev mock support — context defaults to { active: false } when no provider is present
  const devFixtures = useDevFixtures();

  // Data fetching
  const { data: job } = useFetchJob(jobId ?? "");
  const {
    data: nextPendingTask,
    isLoading: taskLoading,
    isFetching: taskFetching,
    isError: taskError,
  } = useNextPendingTask(jobId);

  // When play mode isn't active (or is already exhausted), just navigate
  // away -- the actual "fetch the next job" attempt is wired up below, once
  // activePlanogramId is available (see useFetchNextPlayModeJob).
  // Whether this visit was served a task at all — if so, running out means
  // the labeler finished the job rather than finding it already taken.
  const servedTaskRef = useRef(false);
  if (nextPendingTask) servedTaskRef.current = true;
  const noTaskReason = servedTaskRef.current ? "finished" : "unavailable";

  const bounceToJobs = useBounceToJobs();
  useEffect(() => {
    if (!taskError) return;
    if (!isPlayMode || noNextJobAvailable) {
      bounceToJobs("coverage-qa", jobId ?? "", noTaskReason);
    }
  }, [
    taskError,
    isPlayMode,
    noNextJobAvailable,
    bounceToJobs,
    jobId,
    noTaskReason,
  ]);

  const event = nextPendingTask?.data;
  const { isLoading: parcelLoading } = useFetchParcel(
    event?.parcel_id as string | undefined,
  );

  // Extract original result from task data
  const originalResult = useMemo(() => {
    return event?.original_result as
      | {
          box?: { points: number[][] };
          product_selection?: ProductSelection;
          empty_areas?: Array<{ points: number[][] }>;
          product_selections?: ProductSelection[];
        }
      | undefined;
  }, [event?.original_result]);

  // Get the single box and its product selection
  const boxPoints = useMemo(
    () => extractBoxPoints(originalResult),
    [originalResult],
  );
  const originalProductSelection = useMemo(
    () => extractOriginalProductSelection(originalResult),
    [originalResult],
  );

  // Fetch shelf image
  const {
    mutate: mainImageMutation,
    data: mainImageGallery,
    isPending: mainImageLoading,
  } = useImageGallery();

  // Dev mock: use fixture image gallery data instead of firing the mutation
  const devImageGallery =
    import.meta.env.DEV && devFixtures.active
      ? (devFixtures.mutationOverrides?.imageGallery as typeof mainImageGallery)
      : undefined;
  const effectiveImageGallery = devImageGallery ?? mainImageGallery;

  const shelfImageUrl = resolveGalleryImageUrl(
    effectiveImageGallery?.images[0],
  );
  const imageIdToFetch = (nextPendingTask?.image_id ?? event?.image_id) as
    | string
    | undefined;

  useEffect(() => {
    if (imageIdToFetch) {
      mainImageMutation({ image_ids: [imageIdToFetch] });
    }
  }, [imageIdToFetch, mainImageMutation]);

  // Planogram state
  const originalPlanogramId = originalProductSelection?.planogram_id ?? "";
  const [selectedPlanogramId, setSelectedPlanogramId] = useState<string>("");
  const [planogramSearchOpen, setPlanogramSearchOpen] = useState(false);

  const activePlanogramId = selectedPlanogramId || originalPlanogramId;
  const { data: planogramData, isLoading: planogramLoading } =
    useAtlasPlanogram(activePlanogramId);

  // Flipping noNextJobAvailable lets the bounce effect above send the labeler
  // back to the table with the no-task notice.
  const onNoNextJobFound = useCallback(() => {
    setNoNextJobAvailable(true);
    dispatch(setIsPlayMode(false));
  }, [dispatch]);

  useFetchNextPlayModeJob({
    enabled: !!taskError && isPlayMode && !noNextJobAvailable,
    jobId,
    jobType: PLAY_MODE_JOB_TYPE["coverage-qa"].jobType,
    route: PLAY_MODE_JOB_TYPE["coverage-qa"].route,
    livePlanogramId: activePlanogramId,
    onNoJobFound: onNoNextJobFound,
  });

  // Product selection state
  const originalProductIds = originalProductSelection?.product_ids ?? [];
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

  // UI state
  const [brightness, setBrightness] = useState(100);
  const [boxesVisible, setBoxesVisible] = useState(true);
  const [resetViewKey, setResetViewKey] = useState(0);
  const [detectionConfirmed, setDetectionConfirmed] = useState(false);

  // Planogram product click store (global Zustand)
  const activePlanoProduct = useActivePlanoProductInfoStore(
    (s) => s.productBoxInfo,
  );
  const resetPlanoProductStore = useActivePlanoProductInfoStore((s) => s.reset);
  const lastConsumedProductRef = useRef<string>("");

  // Track task identity to detect transitions and reinitialize state.
  // Using task ID instead of a boolean ref prevents stale-data races when
  // the query refetch hasn't completed yet after submission.
  const currentTaskId = nextPendingTask?.id;
  const prevTaskIdRef = useRef<string | undefined>(undefined);

  // Keep a ref to originalProductIds so the product-click effect
  // always reads the latest value instead of a stale closure capture.
  const originalProductIdsRef = useRef(originalProductIds);
  originalProductIdsRef.current = originalProductIds;

  // Initialize / reinitialize all local state when task changes
  useEffect(() => {
    if (currentTaskId && currentTaskId !== prevTaskIdRef.current) {
      prevTaskIdRef.current = currentTaskId;
      setSelectedProductIds(
        originalProductIds.length > 0 ? [...originalProductIds] : [],
      );
      setSelectedPlanogramId(originalPlanogramId || "");
      setDetectionConfirmed(false);
      lastConsumedProductRef.current = "";
      resetPlanoProductStore();
    }
  }, [
    currentTaskId,
    originalProductIds,
    originalPlanogramId,
    resetPlanoProductStore,
  ]);

  // Track if changes have been made
  const hasChanges = useMemo(() => {
    const planogramChanged =
      selectedPlanogramId !== "" && selectedPlanogramId !== originalPlanogramId;
    const productsChanged =
      selectedProductIds.length !== originalProductIds.length ||
      selectedProductIds.some((id) => !originalProductIds.includes(id)) ||
      originalProductIds.some((id) => !selectedProductIds.includes(id));
    return planogramChanged || productsChanged;
  }, [
    selectedPlanogramId,
    originalPlanogramId,
    selectedProductIds,
    originalProductIds,
  ]);

  // Build product image and name maps from planogram
  const productImageUrls = useMemo(() => {
    const map = new Map<string, string>();
    if (planogramData?.products) {
      for (const product of planogramData.products) {
        if (product.image_url) {
          map.set(product.product_id, product.image_url);
        }
      }
    }
    return map;
  }, [planogramData?.products]);

  const productNameMap = useMemo(() => {
    const map = new Map<string, string>();
    if (planogramData?.products) {
      for (const product of planogramData.products) {
        map.set(product.product_id, product.description || "Unknown product");
      }
    }
    return map;
  }, [planogramData?.products]);

  // Listen for planogram product clicks — use ref for originalProductIds
  // to avoid stale closures when task data changes between click and effect.
  useEffect(() => {
    if (
      activePlanoProduct.product_id &&
      activePlanoProduct.product_id !== "" &&
      activePlanoProduct.product_id !== lastConsumedProductRef.current
    ) {
      lastConsumedProductRef.current = activePlanoProduct.product_id;
      setSelectedProductIds((prev) => {
        const clickedId = activePlanoProduct.product_id;
        if (prev.includes(clickedId)) {
          return prev.filter((id) => id !== clickedId);
        }
        // Clear original products on first new selection
        const currentOriginals = originalProductIdsRef.current;
        const withoutOriginals = prev.filter(
          (id) => !currentOriginals.includes(id),
        );
        return [...withoutOriginals, clickedId];
      });
      resetPlanoProductStore();
    }
  }, [activePlanoProduct.product_id, resetPlanoProductStore]);

  // Handle planogram change from search modal
  const handlePlanogramModalSubmit = useCallback(
    (slots: PlanogramSlotEdit[]) => {
      const newPlanogram = slots[0]?.planogramId;
      if (newPlanogram) {
        setSelectedPlanogramId(newPlanogram);
        // Clear product selections when planogram changes
        setSelectedProductIds([]);
      }
      setPlanogramSearchOpen(false);
    },
    [],
  );

  // Scope the planogram search to the task's store (job.store), never
  // planogram.store_ids — that's the template's chain-wide deployment list.
  const retailerId = planogramData?.retailer?.id ?? null;
  const storeId = job?.store?.atlas_id ?? null;

  // Submit handlers
  const { mutateAsync: completeJobTask, isPending: isSubmitting } =
    useCompleteJobTask(jobId ?? "", nextPendingTask?.id ?? "");

  const handleSubmit = useCallback(async () => {
    if (!jobId || !nextPendingTask?.id) return;

    let result: CoverageQaResult;
    if (hasChanges) {
      result = {
        status: "corrected",
        corrected_product_ids: selectedProductIds,
        ...(selectedPlanogramId !== originalPlanogramId && selectedPlanogramId
          ? { corrected_planogram_id: selectedPlanogramId }
          : {}),
      };
    } else {
      result = { status: "approved" };
    }

    // Dev mock: log result instead of calling API
    if (import.meta.env.DEV && devFixtures.active) {
      console.log("[DevFixture] Submit result:", result);
      return;
    }

    try {
      await completeJobTask({ result });
      // Invalidate to fetch next task — the task-transition effect
      // will reinitialize all local state when the new task ID arrives.
      queryClient.invalidateQueries({ queryKey: ["nextPendingTask", jobId] });
    } catch (error) {
      console.error("Failed to submit QA result:", error);
    }
  }, [
    jobId,
    nextPendingTask?.id,
    hasChanges,
    selectedProductIds,
    selectedPlanogramId,
    originalPlanogramId,
    completeJobTask,
    queryClient,
    devFixtures.active,
  ]);

  const handleReject = useCallback(
    async (rejection: CoverageQaRejection) => {
      if (!jobId || !nextPendingTask?.id) return;

      const result: CoverageQaResult = { status: "rejected", ...rejection };

      // Dev mock: log result instead of calling API
      if (import.meta.env.DEV && devFixtures.active) {
        console.log("[DevFixture] Reject result:", result);
        return;
      }

      try {
        await completeJobTask({ result });
        queryClient.invalidateQueries({ queryKey: ["nextPendingTask", jobId] });
      } catch (error) {
        console.error("Failed to submit rejection:", error);
      }
    },
    [
      jobId,
      nextPendingTask?.id,
      completeJobTask,
      queryClient,
      devFixtures.active,
    ],
  );

  // Build box overlay for BoxDrawImage
  const existingBoxes = useMemo(() => {
    if (!boxPoints) return [];
    return [{ points: boxPoints }];
  }, [boxPoints]);

  // Box image overlay showing selected product
  const boxImageOverlays = useMemo(() => {
    const map = new Map<number, string>();
    if (selectedProductIds.length > 0) {
      const imageUrl = productImageUrls.get(selectedProductIds[0]);
      if (imageUrl) map.set(0, imageUrl);
    }
    return map;
  }, [selectedProductIds, productImageUrls]);

  // Box product labels for tooltip
  const boxProductLabels = useMemo(() => {
    const map = new Map<number, { name: string; imageUrl?: string }>();
    if (selectedProductIds.length > 0) {
      const count = selectedProductIds.length;
      const firstName =
        productNameMap.get(selectedProductIds[0]) ?? "Selected product";
      const name = count > 1 ? `${firstName} (+${count - 1} more)` : firstName;
      map.set(0, {
        name,
        imageUrl: productImageUrls.get(selectedProductIds[0]),
      });
    }
    return map;
  }, [selectedProductIds, productNameMap, productImageUrls]);

  const isLoading =
    mainImageLoading || taskLoading || taskFetching || parcelLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingOverlay visible />
      </div>
    );
  }

  // Build planogram slot labels for modal
  const planogramSlotLabels = activePlanogramId
    ? [
        {
          aisleIndex: 0,
          planogramId: activePlanogramId,
          label: planogramData?.description ?? "Planogram",
        },
      ]
    : [];

  return (
    <div
      className="relative"
      style={{ marginTop: -HEADER_HEIGHT, height: "100vh" }}
    >
      {/* Top bar */}
      <div
        className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-20 flex items-center justify-between px-4"
        style={{ height: HEADER_HEIGHT }}
      >
        <div className="flex items-center gap-3">
          <ActionIcon
            variant="subtle"
            onClick={() => {
              dispatch(setIsPlayMode(false));
              navigate(jobsPath);
            }}
          >
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Text fw={600}>Coverage QA</Text>
        </div>
        <div className="flex items-center gap-2">
          {isPlayMode && (
            <Button
              variant="subtle"
              color="red"
              size="sm"
              leftSection={<IconPlayerStopFilled size={16} />}
              onClick={() => {
                dispatch(setIsPlayMode(false));
                navigate(`${jobsPath}?tab=coverage-qa`);
              }}
            >
              Play Mode
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              dispatch(setIsPlayMode(false));
              navigate(jobsPath);
            }}
          >
            Exit
          </Button>
        </div>
      </div>

      {/* Left sidebar - Tools only (no drawing classes) */}
      <CoverageSidebar
        drawMode="cursor"
        onDrawModeChange={() => {}}
        brightness={brightness}
        onBrightnessChange={setBrightness}
        boxesVisible={boxesVisible}
        onToggleBoxVisibility={() => setBoxesVisible((v) => !v)}
        onResetView={() => {
          setResetViewKey((k) => k + 1);
          setBrightness(100);
        }}
        onZoomIn={() => zoomRef.current?.zoomIn(0.5)}
        onZoomOut={() => zoomRef.current?.zoomOut(0.5)}
        readOnly
      />

      {/* Main content area - 50/50 split */}
      <div
        className="absolute flex"
        style={{
          top: HEADER_HEIGHT,
          left: COVERAGE_SIDEBAR_WIDTH,
          right: 0,
          bottom: ACTION_BAR_HEIGHT,
        }}
      >
        {/* Left panel: Shelf Image */}
        <div className="relative flex-1 min-w-0">
          {shelfImageUrl && (
            <div
              style={{ filter: `brightness(${brightness}%)` }}
              className="w-full h-full flex justify-center items-center"
            >
              <BoxDrawImage
                key={resetViewKey}
                src={shelfImageUrl}
                alt="Coverage QA Task"
                existingBoxes={boxesVisible ? existingBoxes : []}
                hiddenExistingBoxIndices={new Set()}
                selectedExistingBoxIndex={0}
                onSelectedExistingBoxChange={() => {}}
                userDrawnBoxes={[]}
                hiddenUserDrawnBoxIndices={new Set()}
                onUserDrawnBoxesChange={() => {}}
                selectedUserDrawnBoxIndex={null}
                onSelectedUserDrawnBoxChange={() => {}}
                defaultMode="pan"
                variant="empty"
                enableZoom
                enableDrawing={false}
                enableDeletion={false}
                showModeControls={false}
                zoomRef={zoomRef}
                boxImageOverlays={boxImageOverlays}
                useNumbers
                boxNumberLabels={new Map([[0, 1]])}
                confirmedBoxIndices={new Set([0])}
                boxProductLabels={boxProductLabels}
              />
            </div>
          )}
        </div>

        {/* Right panel: Planogram */}
        <div className="flex-1 min-w-0 bg-white border-l border-gray-200 flex flex-col">
          {/* Header with Change Planogram button */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
            <Text fw={600} size="sm">
              Planogram
            </Text>
            <Button
              size="compact-sm"
              variant="light"
              onClick={() => setPlanogramSearchOpen(true)}
            >
              Change Planogram
            </Button>
          </div>

          {/* Selected products list */}
          {selectedProductIds.length > 0 && (
            <div className="px-3 py-2 border-b border-gray-200 flex flex-col gap-1.5">
              <Text size="xs" c="dimmed" fw={600}>
                {selectedProductIds.length} product
                {selectedProductIds.length !== 1 ? "s" : ""} selected
                {hasChanges && " (modified)"}
              </Text>
              {selectedProductIds.map((id) => (
                <CoverageQaProductCard
                  key={id}
                  productId={id}
                  name={productNameMap.get(id) ?? id.slice(-8)}
                  imageUrl={productImageUrls.get(id)}
                  onRemove={() =>
                    setSelectedProductIds((prev) =>
                      prev.filter((p) => p !== id),
                    )
                  }
                />
              ))}
            </div>
          )}

          {/* Planogram viewer */}
          <div className="flex-1 min-h-0">
            {planogramLoading ? (
              <div className="flex items-center justify-center h-full">
                <LoadingOverlay visible />
              </div>
            ) : activePlanogramId ? (
              <div className="h-full [&>div]:!h-full">
                <PlanogramViewer
                  planogram_id={activePlanogramId}
                  fitContent
                  selectedProductIds={selectedProductIds}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <Text c="dimmed" size="sm">
                  No planogram available
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom action bar */}
      <CoverageQaActionBar
        detectionConfirmed={detectionConfirmed}
        hasChanges={hasChanges}
        hasProducts={selectedProductIds.length > 0}
        isSubmitting={isSubmitting}
        onDetectionOk={() => setDetectionConfirmed(true)}
        onSubmit={handleSubmit}
        onReject={handleReject}
      />

      {/* Planogram Search Modal */}
      <PlanogramSearchModal
        opened={planogramSearchOpen}
        onClose={() => setPlanogramSearchOpen(false)}
        onSubmit={handlePlanogramModalSubmit}
        retailerId={retailerId}
        storeId={storeId}
        currentSlots={planogramSlotLabels}
        preselectedSlot={0}
        showAllToggle
      />
    </div>
  );
};
